import React from 'react'
import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'
import { 
  Eye, 
  GraduationCap, 
  Building2, 
  BookOpen, 
  Users 
} from 'lucide-react'

const Solutions: React.FC = () => {
  const { t } = useTranslation()
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white pt-24">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-blue-600 to-blue-700 text-white py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <motion.h1 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-4xl md:text-5xl font-bold mb-6"
            >
              {t('pages.solutions.hero.title')}
            </motion.h1>
            <motion.p 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-xl md:text-2xl text-blue-100 mb-8"
            >
              {t('pages.solutions.hero.subtitle')}
            </motion.p>
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="text-lg text-blue-200 max-w-3xl mx-auto"
            >
              {t('pages.solutions.hero.description')}
            </motion.p>
          </div>
        </div>
      </section>

      {/* Solutions Grid */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* AI Smart Visual Inventory */}
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="bg-white rounded-2xl shadow-lg p-8 hover:shadow-xl transition-shadow duration-300"
            >
              <div className="flex items-center space-x-4 mb-6">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Eye className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">{t('pages.solutions.aiInventory.title')}</h3>
                  <p className="text-blue-600 font-medium">{t('pages.solutions.aiInventory.subtitle')}</p>
                </div>
              </div>
              
              <p className="text-gray-700 mb-6">
                {t('pages.solutions.aiInventory.description')}
              </p>

              <div className="space-y-3">
                <h4 className="font-semibold text-gray-900">{t('common.keyFeatures')}</h4>
                <ul className="space-y-2 text-sm text-gray-600">
                  {(t('pages.solutions.aiInventory.features', { returnObjects: true }) as string[]).map((feature: string, index: number) => (
                    <li key={index} className="flex items-start space-x-2">
                    <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0" />
                      <span>{feature}</span>
                  </li>
                  ))}
                </ul>
              </div>
            </motion.div>

            {/* K-12 Smart Libraries */}
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="bg-white rounded-2xl shadow-lg p-8 hover:shadow-xl transition-shadow duration-300"
            >
              <div className="flex items-center space-x-4 mb-6">
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                  <GraduationCap className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">{t('pages.solutions.k12Libraries.title')}</h3>
                  <p className="text-green-600 font-medium">{t('pages.solutions.k12Libraries.subtitle')}</p>
                </div>
              </div>
              
              <p className="text-gray-700 mb-6">
                {t('pages.solutions.k12Libraries.description')}
              </p>

              <div className="space-y-3">
                <h4 className="font-semibold text-gray-900">{t('common.keyFeatures')}</h4>
                <ul className="space-y-2 text-sm text-gray-600">
                  {(t('pages.solutions.k12Libraries.features', { returnObjects: true }) as string[]).map((feature: string, index: number) => (
                    <li key={index} className="flex items-start space-x-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0" />
                      <span>{feature}</span>
                  </li>
                  ))}
                </ul>
              </div>
            </motion.div>

            {/* University Library Program */}
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="bg-white rounded-2xl shadow-lg p-8 hover:shadow-xl transition-shadow duration-300"
            >
              <div className="flex items-center space-x-4 mb-6">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <GraduationCap className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">{t('pages.solutions.military.title')}</h3>
                  <p className="text-blue-600 font-medium">{t('pages.solutions.military.subtitle')}</p>
                </div>
              </div>
              
              <p className="text-gray-700 mb-6">
                {t('pages.solutions.military.description')}
              </p>

              <div className="space-y-3">
                <h4 className="font-semibold text-gray-900">{t('common.keyFeatures')}</h4>
                <ul className="space-y-2 text-sm text-gray-600">
                  {(t('pages.solutions.military.features', { returnObjects: true }) as string[]).map((feature: string, index: number) => (
                    <li key={index} className="flex items-start space-x-2">
                    <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0" />
                      <span>{feature}</span>
                  </li>
                  ))}
                </ul>
              </div>
            </motion.div>

            {/* Corporate Reading Spaces */}
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="bg-white rounded-2xl shadow-lg p-8 hover:shadow-xl transition-shadow duration-300"
            >
              <div className="flex items-center space-x-4 mb-6">
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Building2 className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">{t('pages.solutions.corporate.title')}</h3>
                  <p className="text-purple-600 font-medium">{t('pages.solutions.corporate.subtitle')}</p>
                </div>
              </div>
              
              <p className="text-gray-700 mb-6">
                {t('pages.solutions.corporate.description')}
              </p>

              <div className="space-y-3">
                <h4 className="font-semibold text-gray-900">{t('common.keyFeatures')}</h4>
                <ul className="space-y-2 text-sm text-gray-600">
                  {(t('pages.solutions.corporate.features', { returnObjects: true }) as string[]).map((feature: string, index: number) => (
                    <li key={index} className="flex items-start space-x-2">
                    <div className="w-2 h-2 bg-purple-500 rounded-full mt-2 flex-shrink-0" />
                      <span>{feature}</span>
                  </li>
                  ))}
                </ul>
              </div>
            </motion.div>

            {/* Public Libraries */}
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="bg-white rounded-2xl shadow-lg p-8 hover:shadow-xl transition-shadow duration-300"
            >
              <div className="flex items-center space-x-4 mb-6">
                <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center">
                  <BookOpen className="w-6 h-6 text-indigo-600" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">{t('pages.solutions.publicLibraries.title')}</h3>
                  <p className="text-indigo-600 font-medium">{t('pages.solutions.publicLibraries.subtitle')}</p>
                </div>
              </div>
              
              <p className="text-gray-700 mb-6">
                {t('pages.solutions.publicLibraries.description')}
              </p>

              <div className="space-y-3">
                <h4 className="font-semibold text-gray-900">{t('common.keyFeatures')}</h4>
                <ul className="space-y-2 text-sm text-gray-600">
                  {(t('pages.solutions.publicLibraries.features', { returnObjects: true }) as string[]).map((feature: string, index: number) => (
                    <li key={index} className="flex items-start space-x-2">
                      <div className="w-2 h-2 bg-indigo-500 rounded-full mt-2 flex-shrink-0" />
                      <span>{feature}</span>
                  </li>
                  ))}
                </ul>
              </div>
            </motion.div>

            {/* Specialized Libraries */}
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="bg-white rounded-2xl shadow-lg p-8 hover:shadow-xl transition-shadow duration-300"
            >
              <div className="flex items-center space-x-4 mb-6">
                <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                  <Users className="w-6 h-6 text-orange-600" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">{t('pages.solutions.specializedLibraries.title')}</h3>
                  <p className="text-orange-600 font-medium">{t('pages.solutions.specializedLibraries.subtitle')}</p>
                </div>
              </div>
              
              <p className="text-gray-700 mb-6">
                {t('pages.solutions.specializedLibraries.description')}
              </p>

              <div className="space-y-3">
                <h4 className="font-semibold text-gray-900">{t('common.keyFeatures')}</h4>
                <ul className="space-y-2 text-sm text-gray-600">
                  {(t('pages.solutions.specializedLibraries.features', { returnObjects: true }) as string[]).map((feature: string, index: number) => (
                    <li key={index} className="flex items-start space-x-2">
                      <div className="w-2 h-2 bg-orange-500 rounded-full mt-2 flex-shrink-0" />
                      <span>{feature}</span>
                  </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-gradient-to-r from-blue-600 to-blue-700 text-white py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <motion.h2 
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-3xl md:text-4xl font-bold mb-6"
            >
              {t('common.cta.readyToTransform')}
            </motion.h2>
            <motion.p 
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-xl text-blue-100 mb-8"
            >
              {t('common.cta.contactDescription')}
            </motion.p>
            <motion.button
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="bg-white text-blue-600 px-8 py-4 rounded-lg font-semibold hover:bg-blue-50 transition-colors duration-200"
            >
              {t('common.cta.getFreeConsultation')}
            </motion.button>
          </div>
        </div>
      </section>
    </div>
  )
}

export default Solutions 